<template>
  <div class="stack_tool_container">
    
    <div class="left">
      <!-- 청구사례 BEST -->
      <div class="section best-fp" v-if="claimExList.length > 0">
        <div class="header"> 
          <h2>📋 인기 청구사례</h2>
          <span @click="openClaimExList">더보기 <i class="ph ph-caret-right"></i></span>
        </div>
        <ul class="blocks img-blocks">
          <li v-for="post in claimExList" :key="post.idx" @click="openClaimExView(post.idx)">
            <img v-if="getFirstImage(post.images)" :src="getFirstImage(post.images)" />
            <div class="placeholder" v-else><i class="ph ph-clipboard-text"></i></div>
            <h2>{{ post.title }}</h2>
            <div class="status">
              <span><i class="ph ph-heart"></i> {{ post.like_count || 0 }}</span>
              <span><i class="ph ph-chat-circle"></i> {{ post.comment_count || 0 }}</span>
            </div>
          </li>
        </ul>
      </div>

      <!-- 청구사례 빈 상태 -->
      <div class="section best-fp empty-section" v-else @click="openClaimExList">
        <div class="header"> 
          <h2>📋 인기 청구사례</h2>
        </div>
        <div class="empty-state">
          <i class="ph ph-clipboard-text"></i>
          <p>등록된 사례가 없습니다</p>
        </div>
      </div>

      <!-- 시책 -->
      <div class="section policy">
        <div class="header"> 
          <h2>⚡ 시책</h2> 
          <span @click="props.oth_showPolicyList(true)">전체보기 <i class="ph ph-caret-right"></i></span> 
        </div>

        <!-- 로딩 -->
        <div v-if="policyLoading" class="policy-loading">
          <i class="fas fa-spinner fa-spin"></i>
        </div>

        <!-- 빈 상태 -->
        <div v-else-if="policy_list.length === 0" class="policy-empty">
          <i class="ph ph-clipboard"></i>
          <p>이번 달 시책이 없습니다</p>
          <button @click="props.oth_showPolicyList(true)">시책 등록하기</button>
        </div>

        <!-- 시책 목록 -->
        <ul v-else class="policy-list">
          <li v-for="(policy, i) in policy_list" :key="i" @click="props.oth_showPolicyList(true)">
            <div class="company-logo">
              <img v-if="policy.image" :src="policy.image" />
              <div v-else class="logo-placeholder">{{ policy.company_name?.charAt(0) || '?' }}</div>
            </div>
            <div class="policy-info">
              <h3>{{ policy.title }}</h3>
              <span class="company-name">{{ policy.company_name }}</span>
            </div>
            <div class="policy-right">
              <div class="badge" :class="getStatusClass(policy)">{{ getStatusText(policy) }}</div>
              <span class="amount">{{ policy.amount }}%</span>
            </div>
          </li>
        </ul>
      </div>

      <!-- 하단 메뉴 -->
      <div class="bottom-menu">
        <button class="menu-btn" @click="props.oth_showBoardMain(true)">
          <i class="ph-fill ph-books"></i>
          <span>게시판</span>
        </button>
        <button class="menu-btn" @click="props.oth_showCurriculum(true)">
          <i class="ph-fill ph-graduation-cap"></i>
          <span>교육</span>
        </button>
        <button class="menu-btn" @click="props.oth_showInsuAccount(true)">
          <i class="ph-fill ph-identification-badge"></i>
          <span>계정정보</span>
        </button>
        <button class="menu-btn" @click="props.oth_showGuide(true)">
          <i class="ph-fill ph-question"></i>
          <span>가이드북</span>
        </button>
      </div>
    </div>

    <div class="right">
      <!-- 교육 -->
      <div class="section education" v-if="curriculumList.length > 0">
        <div class="header"> 
          <h2>🎓 교육</h2> 
          <span @click="openCurriculum">더보기 <i class="ph ph-caret-right"></i></span> 
        </div>
        <ul class="lines">
          <li v-for="(lesson, i) in curriculumList" :key="i" @click="openCurriculum">
            <div class="line-left">
              <span class="type-icon">
                <i :class="lesson.content_type === 'video' ? 'ph ph-play-circle' : 'ph ph-article'"></i>
              </span>
              <div class="line-info">
                <p>{{ lesson.title }}</p>
                <small v-if="lesson.duration">{{ formatDuration(lesson.duration) }}</small>
              </div>
            </div>
            <i class="ph ph-caret-right line-arrow"></i>
          </li>
        </ul>
      </div>

      <!-- 교육 빈 상태 -->
      <div class="section education empty-section" v-else @click="openCurriculum">
        <div class="header"> 
          <h2>🎓 교육</h2>
        </div>
        <div class="empty-state">
          <i class="ph ph-book-open"></i>
          <p>준비된 교육이 없습니다</p>
        </div>
      </div>

      <!-- 내자료 -->
      <div class="section my-data">
        <div class="header"> 
          <h2>📂 내 자료</h2> 
          <span @click="oth_showMyFiles(true)">전체보기 <i class="ph ph-caret-right"></i></span> 
        </div>

        <!-- 용량 표시 -->
        <div class="storage-bar-mini">
          <div class="bar-track">
            <div 
              class="bar-fill" 
              :style="{ width: storagePercent + '%' }"
              :class="{ warning: storagePercent > 80, danger: storagePercent > 95 }"
            ></div>
          </div>
          <span class="storage-text">{{ formatFileSize(storageUsed) }} / {{ formatFileSize(storageLimit) }}</span>
        </div>

        <!-- 파일 없을 때 -->
        <div class="empty-files" v-if="my_files.length === 0">
          <i class="ph ph-folder-open"></i>
          <p>등록된 자료가 없습니다.</p>
        </div>

        <!-- 파일 그리드 -->
        <div class="file-grid" v-else>
          <div 
            class="file-item" 
            v-for="(file, i) in my_files" 
            :key="i"
            @click="handleFileClick(file)"
          >
            <!-- 이미지 파일 -->
            <img v-if="isImageFile(file.type)" :src="file.path" :alt="file.title" />
            <!-- 문서 파일 -->
            <div v-else class="file-icon">
              <i :class="getFileIcon(file.type)"></i>
              <span>{{ file.title }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 이미지 미리보기 모달 -->
    <div class="image-preview-modal" v-if="previewImage" @click="closePreview">
      <div class="preview-container" @click.stop>
        <button class="preview-close" @click="closePreview">
          <i class="ph ph-x"></i>
        </button>
        <img :src="previewImage.path" :alt="previewImage.title" />
        <div class="preview-info">
          <span class="preview-title">{{ previewImage.title }}</span>
          <span class="preview-meta">{{ formatFileSize(previewImage.size) }}</span>
        </div>
        <div class="preview-actions">
          <button @click="downloadFile(previewImage)">
            <i class="ph ph-download-simple"></i> 다운로드
          </button>
          <button @click="openFile(previewImage)">
            <i class="ph ph-arrow-square-out"></i> 새 탭
          </button>
        </div>
      </div>
    </div>

    <!-- 청구사례 상세보기 -->
    <ClaimExView 
      v-if="showClaimExView" 
      :oth_showClaimExView="toggleClaimExView"
      :postIdx="claimExViewIdx"
    />

  </div>
</template>

<script setup>
import { ref, inject, onMounted, defineProps } from 'vue';
import ClaimExView from '@/components/dialogs/claimExView.vue';

const props = defineProps({
  oth_showPolicyList: { type: Function, required: true },
  oth_showMyFiles: { type: Function, required: true },
  oth_showClaimExList: { type: Function, required: true },
  oth_showCurriculum: { type: Function, required: true },
  oth_showBoardMain: { type: Function, required: true },
  oth_showInsuAccount: { type: Function, required: true },
  oth_showGuide: { type: Function, required: true }
})

const sql_query = inject('sql_query');
const member_info = inject('member_info');

// 청구사례 데이터
const claimExList = ref([]);
const claimExLoading = ref(false);

// 청구사례 View 다이얼로그 상태
const showClaimExView = ref(false);
const claimExViewIdx = ref(null);

// 교육 데이터
const curriculumList = ref([]);
const curriculumLoading = ref(false);

// 시책 데이터
const policy_list = ref([]);
const policyLoading = ref(false);

// 내 자료
const my_files = ref([]);
const previewImage = ref(null);

// 용량 관련
const storageUsed = ref(0);
const storageLimit = ref(1 * 1024 * 1024 * 1024);
const storagePercent = ref(0);

// 청구사례 카테고리
// const categories = [
//   { label: '전체', value: 'all' },
//   { label: '암', value: 'cancer' },
//   { label: '자동차', value: 'car' },
//   { label: '3대질병', value: 'major3' },
//   { label: '실손', value: 'actual' },
//   { label: '기타', value: 'etc' }
// ];

// 카테고리 라벨 가져오기
// function getCategoryLabel(value) {
//   const cat = categories.find(c => c.value === value);
//   return cat ? cat.label : '기타';
// }

// 첫 번째 이미지 가져오기
function getFirstImage(imagesJson) {
  if (!imagesJson) return null;
  try {
    const images = JSON.parse(imagesJson);
    return images.length > 0 ? images[0] : null;
  } catch {
    return null;
  }
}

// 청구사례 BEST 조회 (인기순 3개)
async function fetchClaimEx() {
  claimExLoading.value = true;
  try {
    const rows = await sql_query(`
      SELECT idx, mb_id, title, content, category, images, like_count, comment_count, created_at
      FROM claim_ex_post
      ORDER BY like_count DESC, comment_count DESC, created_at DESC
      LIMIT 3
    `);
    claimExList.value = rows || [];
  } catch (e) {
    console.error('청구사례 조회 오류:', e);
  } finally {
    claimExLoading.value = false;
  }
}

// 교육 커리큘럼 조회 (최근 레슨 3개)
async function fetchCurriculum() {
  curriculumLoading.value = true;
  try {
    const rows = await sql_query(`
      SELECT idx, title, content_type, duration, sort_order
      FROM curriculum_items 
      WHERE type = 'lesson' AND is_active = 1
      ORDER BY sort_order ASC
      LIMIT 3
    `);
    curriculumList.value = rows || [];
  } catch (e) {
    console.error('교육 조회 오류:', e);
  } finally {
    curriculumLoading.value = false;
  }
}

// 시간 포맷
function formatDuration(seconds) {
  if (!seconds) return '';
  const min = Math.floor(seconds / 60);
  if (min < 60) return `${min}분`;
  const hour = Math.floor(min / 60);
  const remMin = min % 60;
  return `${hour}시간 ${remMin}분`;
}

// 청구사례 View 토글
function toggleClaimExView(show, options = {}) {
  if (show) {
    claimExViewIdx.value = options.postIdx;
    showClaimExView.value = true;
  } else {
    showClaimExView.value = false;
    claimExViewIdx.value = null;
    if (options.refresh) {
      fetchClaimEx();
    }
  }
}

// 청구사례 개별 View 열기
function openClaimExView(idx) {
  claimExViewIdx.value = idx;
  showClaimExView.value = true;
}

// 청구사례 List 열기
function openClaimExList() {
  if (props.oth_showClaimExList) {
    props.oth_showClaimExList(true);
  }
}

// 교육 List 열기
function openCurriculum() {
  if (props.oth_showCurriculum) {
    props.oth_showCurriculum(true);
  }
}

// 이번 달 시책 조회
async function fetchPolicies() {
  policyLoading.value = true;
  try {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const firstDay = `${year}-${month}-01`;
    const lastDay = `${year}-${month}-${new Date(year, now.getMonth() + 1, 0).getDate()}`;

    const sql = `
      SELECT p.*, c.name as company_name, c.short_img, c.type as company_type
      FROM insu_policies p
      LEFT JOIN insu_company c ON p.company_idx = c.idx
      WHERE
        (
          (p.start_date <= '${lastDay}' AND p.expire_date >= '${firstDay}')
          OR (p.start_date BETWEEN '${firstDay}' AND '${lastDay}')
        )
      ORDER BY 
        CASE 
          WHEN CURDATE() BETWEEN p.start_date AND p.expire_date THEN 0
          WHEN p.start_date > CURDATE() THEN 1
          ELSE 2
        END,
        p.start_date ASC
      LIMIT 5
    `;

    policy_list.value = await sql_query(sql);
  } finally {
    policyLoading.value = false;
  }
}

// 상태 계산
function getStatus(policy) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const start = new Date(policy.start_date);
  const end = new Date(policy.expire_date);
  
  if (today < start) return 'upcoming';
  if (today > end) return 'ended';
  return 'active';
}

function getStatusClass(policy) {
  return getStatus(policy);
}

function getStatusText(policy) {
  const map = { upcoming: '예정', active: '진행중', ended: '종료' };
  return map[getStatus(policy)];
}

// 파일 목록 조회
async function fetchMyFiles() {
  let sql = `
    SELECT * FROM files 
    WHERE uploaded_by = '${member_info.mb_id}'
    AND target_table = 'customers'
    AND status = 1
    ORDER BY created_at DESC
    LIMIT 12
  `;
  let result = await sql_query(sql);
  my_files.value = result || [];
}

// 용량 조회
async function fetchStorageInfo() {
  try {
    const response = await fetch('/api/user/storage', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ user: member_info.mb_id })
    });
    const data = await response.json();
    if (data.success) {
      storageUsed.value = data.storage.used;
      storageLimit.value = data.storage.limit;
      storagePercent.value = parseFloat(data.storage.usedPercent);
    }
  } catch (err) {
    console.error('용량 조회 실패:', err);
  }
}

// 이미지 파일 체크
function isImageFile(type) {
  if (!type) return false;
  return type.startsWith('image/');
}

// 파일 아이콘
function getFileIcon(type) {
  if (!type) return 'ph ph-file';
  if (type.includes('pdf')) return 'ph ph-file-pdf';
  if (type.includes('word') || type.includes('document')) return 'ph ph-file-doc';
  if (type.includes('excel') || type.includes('sheet')) return 'ph ph-file-xls';
  if (type.includes('powerpoint') || type.includes('presentation')) return 'ph ph-file-ppt';
  if (type.includes('text')) return 'ph ph-file-text';
  return 'ph ph-file';
}

// 파일 크기 포맷
function formatFileSize(bytes) {
  if (!bytes) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}

// 파일 클릭 핸들러
function handleFileClick(file) {
  if (isImageFile(file.type)) {
    previewImage.value = file;
    document.body.style.overflow = 'hidden';
  } else {
    openFile(file);
  }
}

// 미리보기 닫기
function closePreview() {
  previewImage.value = null;
  document.body.style.overflow = '';
}

// 파일 열기
function openFile(file) {
  window.open(file.path, '_blank');
}

// 파일 다운로드
function downloadFile(file) {
  const link = document.createElement('a');
  link.href = file.path;
  link.download = file.title;
  link.click();
}

onMounted(() => {
  fetchClaimEx();
  fetchCurriculum();
  fetchPolicies();
  fetchMyFiles();
  fetchStorageInfo();
});
</script>

<style scoped>
/* 기존 스타일 유지 + 추가 스타일 */
#consered {
  opacity: 0.6;
  filter: blur(1px);
}

.stack_tool_container {
  width: 100%;
  height: 100%;
  display: flex;
  gap: 24px;
  box-sizing: border-box;
  color:#fff;
}

.left {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-width: 0;
  height: 100%;
}

.right {
  width: 400px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  height: 100%;
  overflow-y: auto;
}

.right::-webkit-scrollbar {
  width: 4px;
}

.right::-webkit-scrollbar-thumb {
  background: var(--color-text-secondary);
  border-radius: 2px;
}

.section {
  background: rgba(0,0,0,0.4);
  backdrop-filter: blur(var(--blur));
  border-radius: var(--radius-lg);
  padding: 18px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.section.best-fp {
  flex: 1;
  min-height: 0;
}

.section.policy {
  flex: 1.4;
  min-height: 0;
}

.section.education,
.section.my-data {
  flex: 1;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 14px;
  flex-shrink: 0;
}

.header h2 {
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-bold);
  margin: 0;
  color: #fff;
}

.header span {
  font-size: var(--font-size-base);
  color: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  opacity: 0.6;
  transition: opacity 0.2s;
}

.header span:hover {
  opacity: 1;
}

/* Blocks (그리드형) - 청구사례 */
ul.blocks {
  width: 100%;
  display: flex;
  align-items: stretch;
  justify-content: flex-start;
  gap: 12px;
  list-style: none;
  padding: 0;
  margin: 0;
  flex: 1;
  overflow-x: auto;
}

ul.blocks::-webkit-scrollbar {
  height: 0;
}

ul.blocks > li {
  cursor: pointer;
  min-width: 140px;
  max-width: 160px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  transition: transform 0.2s;
}

ul.blocks > li:hover {
  transform: translateY(-4px);
}

ul.blocks > li > img {
  width: 100%;
  aspect-ratio: 1/1;
  border-radius: 12px;
  background: var(--color-surface-light);
  object-fit: cover;
}

ul.blocks > li > .placeholder {
  width: 100%;
  aspect-ratio: 1/1;
  border-radius: 12px;
  background: var(--color-surface-light);
  display: flex;
  align-items: center;
  justify-content: center;
}

ul.blocks > li > .placeholder > i {
  font-size: 32px;
  color: var(--color-text-third);
}

ul.blocks > li > h2 {
  font-size: 13px;
  font-weight: 700;
  color: #fff;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  margin: 0;
}

/* Lines (리스트형) - 교육 */
ul.lines {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 8px;
  list-style: none;
  padding: 0;
  margin: 0;
  flex: 1;
  overflow-y: auto;
}

ul.lines > li {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  cursor: pointer;
  padding: 12px 14px;
  background: var(--color-surface-light);
  border-radius: 10px;
  transition: all 0.2s;
  box-sizing: border-box;
}

ul.lines > li:hover {
  background: var(--color-surface-reverse);
}

ul.lines .line-left {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
  min-width: 0;
}

ul.lines .type-icon {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: var(--color-brand-light);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

ul.lines .type-icon > i {
  font-size: 16px;
  color: var(--color-brand);
}

ul.lines .line-info {
  flex: 1;
  min-width: 0;
}

ul.lines .line-info > p {
  font-size: 14px;
  font-weight: 600;
  color: #fff;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin: 0;
}

ul.lines .line-info > small {
  font-size: 12px;
  color: var(--color-text-secondary);
}

ul.lines .line-arrow {
  color: var(--color-text-secondary);
  font-size: 16px;
}

/* Status */
.status {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 10px;
  font-size: 12px;
}

.status > span {
  display: flex;
  align-items: center;
  gap: 4px;
  color: var(--color-text-secondary);
}

/* Empty State */
.empty-section {
  cursor: pointer;
}

.empty-state {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 20px;
}

.empty-state > i {
  font-size: 36px;
  color: var(--color-text-third);
  opacity: 0.5;
}

.empty-state > p {
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
  margin: 0;
}

/* 시책 로딩 & 빈 상태 */
.policy-loading {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-text-secondary);
  font-size: var(--font-size-xl);
}

.policy-empty {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  color: var(--color-text-secondary);
}

.policy-empty i {
  font-size: 48px;
  opacity: 0.5;
}

.policy-empty p {
  margin: 0;
  font-size: var(--font-size-base);
}

.policy-empty button {
  margin-top: 8px;
  padding: 10px 20px;
  background: var(--color-brand);
  border: none;
  border-radius: var(--radius-md);
  color: #fff;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  cursor: pointer;
  transition: all 0.2s;
}

.policy-empty button:hover {
  filter: brightness(1.1);
}

/* 시책 리스트 */
.policy-list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
  overflow-y: auto;
  flex: 1;
}

.policy-list::-webkit-scrollbar {
  width: 4px;
}

.policy-list::-webkit-scrollbar-thumb {
  background: var(--color-text-secondary);
  border-radius: 2px;
}

.policy-list li {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: rgba(0,0,0,0.4);
  border-radius: var(--radius-md);
  box-sizing: border-box;
  cursor: pointer;
  transition: all 0.2s;
  border: 1px solid transparent;
}

.policy-list li:hover {
  border-color: var(--color-brand);
  background: var(--color-surface);
}

.company-logo {
  width: 40px;
  height: 40px;
  border-radius: var(--radius-md);
  overflow: hidden;
  flex-shrink: 0;
  background: var(--color-background);
  display: flex;
  align-items: center;
  justify-content: center;
}

.company-logo img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  padding: 4px;
  box-sizing: border-box;
}

.company-logo .logo-placeholder {
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-bold);
  color: var(--color-text-secondary);
}

.policy-info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.policy-info h3 {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  margin: 0;
  color: #fff;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.policy-info .company-name {
  font-size: var(--font-size-xs);
  color: var(--color-text-secondary);
}

.policy-right {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
}

.badge {
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-semibold);
  padding: 4px 10px;
  border-radius: var(--radius-md);
}

.badge.active {
  background: rgba(16, 185, 129, 0.15);
  color: #10b981;
}

.badge.upcoming {
  background: rgba(51, 170, 255, 0.15);
  color: var(--color-special-point);
}

.badge.ended {
  background: var(--color-surface-reverse);
  color: var(--color-text-secondary);
}

.policy-right .amount {
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-bold);
  color: var(--color-brand);
}

/* 하단 메뉴 */
.bottom-menu {
  display: flex;
  gap: 12px;
}

.menu-btn {
  flex: 1;
  height: 80px;
  background: #123A5A;
  backdrop-filter: blur(var(--blur));
  border: none;
  border-radius: var(--radius-lg);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  cursor: pointer;
  color: #fff;
  transition: all 0.2s;
}

.menu-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.menu-btn i {
  font-size: var(--font-size-2xl);
}

.menu-btn span {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
}

/* 교육 */
.edu-info {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.edu-card {
  background: rgba(0,0,0,0.4);
  border-radius: var(--radius-md);
  padding: 12px;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  gap: 12px;
}

.edu-card .edu-type-icon {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--color-brand-light);
  border-radius: var(--radius-sm);
  flex-shrink: 0;
}

.edu-card .edu-type-icon i {
  font-size: var(--font-size-lg);
  color: var(--color-brand);
}

.edu-card .label {
  font-size: var(--font-size-sm);
  color: #fff;
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.edu-card .duration {
  font-size: var(--font-size-xs);
  color: var(--color-text-secondary);
  flex-shrink: 0;
}

/* 교육 로딩 & 빈 상태 */
.edu-loading {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-text-secondary);
  font-size: var(--font-size-xl);
  padding: 20px;
}

.edu-loading i {
  animation: spin 1s linear infinite;
}

.edu-empty {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: var(--color-text-secondary);
  padding: 20px;
}

.edu-empty i {
  font-size: 32px;
  opacity: 0.5;
}

.edu-empty p {
  font-size: var(--font-size-sm);
  margin: 0;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.reactions {
  display: flex;
  gap: 12px;
  font-size: var(--font-size-xs);
  color: var(--color-text-secondary);
}

.reactions span {
  display: flex;
  align-items: center;
  gap: 4px;
}

/* 용량 바 미니 */
.storage-bar-mini {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
}

.bar-track {
  flex: 1;
  height: 6px;
  background: var(--color-surface);
  border-radius: 3px;
  overflow: hidden;
}

.bar-fill {
  height: 100%;
  background: var(--color-brand);
  border-radius: 3px;
  transition: width 0.3s;
}

.bar-fill.warning {
  background: var(--color-special-warning);
}

.bar-fill.danger {
  background: #e53935;
}

.storage-text {
  font-size: var(--font-size-xs);
  color: var(--color-text-secondary);
  white-space: nowrap;
}

/* 빈 파일 상태 */
.empty-files {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 32px;
  gap: 12px;
}

.empty-files i {
  font-size: 48px;
  color: var(--color-text-secondary);
  opacity: 0.5;
}

.empty-files p {
  margin: 0;
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
}

/* 내자료 */
.file-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 10px;
}

.file-item {
  aspect-ratio: 1;
  background: rgba(0,0,0,0.4);
  border-radius: var(--radius-md);
  overflow: hidden;
  cursor: pointer;
  transition: all 0.2s;
  border: 2px solid transparent;
}

.file-item:hover {
  transform: scale(1.05);
  border-color: var(--color-brand);
}

.file-item img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.file-icon {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 8px;
  box-sizing: border-box;
}

.file-icon i {
  font-size: var(--font-size-xl);
  color: var(--color-brand);
}

.file-icon span {
  font-size: 9px;
  color: var(--color-text-secondary);
  text-align: center;
  word-break: break-all;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* 이미지 미리보기 모달 */
.image-preview-modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.9);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 99999;
  padding: 20px;
  box-sizing: border-box;
}

.preview-container {
  position: relative;
  max-width: 90%;
  max-height: 90%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}

.preview-container img {
  max-width: 100%;
  max-height: 70vh;
  object-fit: contain;
  border-radius: var(--radius-md);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5);
}

.preview-close {
  position: absolute;
  top: -40px;
  right: 0;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.1);
  border: none;
  border-radius: 50%;
  color: #fff;
  font-size: 20px;
  cursor: pointer;
  transition: all 0.2s;
}

.preview-close:hover {
  background: rgba(255, 255, 255, 0.2);
}

.preview-info {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}

.preview-title {
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-semibold);
  color: #fff;
}

.preview-meta {
  font-size: var(--font-size-sm);
  color: rgba(255, 255, 255, 0.6);
}

.preview-actions {
  display: flex;
  gap: 12px;
}

.preview-actions button {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: var(--radius-md);
  color: #fff;
  font-size: var(--font-size-sm);
  cursor: pointer;
  transition: all 0.2s;
}

.preview-actions button:hover {
  background: var(--color-brand);
  border-color: var(--color-brand);
  color: var(--color-text-black);
}

@media (max-width: 1024px), (max-height: 768px) {
  /* 명예의 FP */
  .fp-cards {
    height:100%;
    display: flex;
    gap: 6px;
    overflow: hidden !important;
  }

  .fp-cards::-webkit-scrollbar {
    height: 0px;
    width:0;
  }

  .fp-cards::-webkit-scrollbar-thumb {
    display:none;
  }

  .fp-card {
    min-width: 100px;
    background: rgba(0,0,0,0.4);
    border-radius: var(--radius-lg);
    padding: 12px;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  .fp-card .profile {
    width:100%;
    display:flex;
    align-items: center;
    justify-content: center;
    gap:6px;
  }

  .fp-card .profile img {
    width: 16px;
    height: 16px;
    border-radius: 50%;
    object-fit: cover;
  }

  .fp-card .profile h3 {
    width:100%;
    font-size: 14px;
    font-weight: var(--font-weight-bold);
    margin: 0;
    color: #fff;
  }

  .fp-card p {
    font-size: 12px;
    color: #fff;
    margin: 0;
    line-height: 1.4;
  }

  .fp-card .stats {
    display: flex;
    justify-content: end;
    gap: 12px;
    font-size: var(--font-size-sm);
    color: #fff;
  }

  .fp-card .stats span {
    display: flex;
    align-items: center;
    gap: 4px;
  }
}

/* #### Portrait 모드 (태블릿/모바일 세로) #### */
@media (max-width: 1024px) and (orientation: portrait) {
  .stack_tool_container {
    flex-direction: column;
    gap: 12px;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
  }

  .left {
    flex: none;
    height: auto;
    gap: 12px;
  }

  .right {
    width: 100% !important;
    height: auto;
    flex: none;
    gap: 10px;
    overflow-y: visible;
  }

  .section.best-fp { flex: none; min-height: 160px; }
  .section.policy { flex: none; min-height: 200px; }
  .section.education { flex: none; min-height: 120px; }
  .section.my-data { flex: none; min-height: 120px; }
}
</style>